/* PACKAGES */
import React, { Component } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import Link from 'redux-first-router-link';
import * as Sentry from '@sentry/browser';

/* ELEMENTS */
import SizeChart from './SizeChart';
import Input from '../Elements/Input/Input';
import Button from '../Elements/Button/Button';
import Price from './Price';
import Radio from '../Elements/Radio/Radio';
import cartAnalytics from '../Selection/cartAnalytics';

/* DATA */
import { preloadPage, goToPage } from '../../actions/environment';
import { addToBag, changeAddressInput, productAlert, preSelectSize } from '../../actions/checkout';

/* UTILS */
import { scrollTo, offset, addEvent, removeEvent } from '../../utils/dom';
import { imageSizeTranslation } from '../../utils/image';
import { translate, prefix } from '../../utils/locale';
import { urlify, splitNewLine } from '../../utils/string';

/* STYLING */
import s from './Purchase.scss';

class Purchase extends Component {
	constructor(props) {
		super(props);

		const { setup, sizes, productId, checkout, relatedProducts, productCache } = props;
		const { preSelectedSize = '' } = checkout;
		const { products = {} } = productCache[setup.market][setup.pricelist];
		const [product] = sizes.length === 1 ? sizes[0].item.split('-') : [];
		const [preSelectedProduct] = preSelectedSize.split('-');
		const selected = {};
		let selectedItemSoldOut = false;
		let testPreselectedSizes = sizes;

		if (preSelectedProduct && (relatedProducts?.size && relatedProducts.size.indexOf(preSelectedProduct) !== -1) && products[preSelectedProduct]) {
			testPreselectedSizes = products[preSelectedProduct].sizes;
		}

		let hasPreselectedSize = testPreselectedSizes.filter((size) => {
			if (size.item === preSelectedSize && size.stock === 0) selectedItemSoldOut = preSelectedSize;
			return size.item === preSelectedSize;
		}).length > 0;

		if (relatedProducts.bundle) {
			relatedProducts.bundle.forEach((extraProduct) => {
				const bundleProduct = products[extraProduct];
				if (bundleProduct.sizes.length === 1) selected[extraProduct] = bundleProduct.sizes[0].item;
				else selected[extraProduct] = false;
			});
			if (relatedProducts.bundle.length > 1 && preSelectedProduct === productId && hasPreselectedSize) hasPreselectedSize = false;
		}

		if (product) {
			selected[product] = sizes[0].item;
		} else if (hasPreselectedSize) {
			selected[preSelectedProduct] = preSelectedSize;
		} else if (!relatedProducts.bundle || relatedProducts.bundle.length < 2) {
			selected[productId] = false;
		}

		this.state = {
			formError: false,
			formErrorHover: false,
			showPopup: false,
			showNotifyThanks: false,
			stickyPurchase: setup.breakPoint === 'mobile',
			selected,
			selectedItemSoldOut,
			variantSelected: !relatedProducts.configurator,
		};
	}

	componentDidMount() {
		const { setup, relatedProducts = {}, productCache } = this.props;
		const { products = {} } = productCache[setup.market][setup.pricelist];
		if (relatedProducts.configurator) {
			const preloadProductPaths = relatedProducts.configurator.filter((p) => products?.[p]?.canonicalUri).map((p) => products?.[p]?.canonicalUri);
			if (preloadProductPaths.length > 0) this.props.preloadPage(preloadProductPaths);
		}

		addEvent(window, 'scroll.stickyPurchase', this.checkStickyPurchase);
	}

	componentWillUnmount() {
		removeEvent(window, 'scroll.stickyPurchase', this.checkStickyPurchase);
	}

	checkStickyPurchase = () => {
		const { stickyPurchase } = this.state;
		const scrollPosition = window.scrollY;
		const purchaseButton = document.getElementById('js-purchaseButtonContainer');
		const purchaseButtonPosition = purchaseButton ? offset(purchaseButton).top + 68 : 0;

		if (scrollPosition + window.innerHeight > purchaseButtonPosition && scrollPosition < purchaseButtonPosition + 50 && stickyPurchase) {
			this.setState({
				stickyPurchase: false,
			});
		} else if ((scrollPosition + window.innerHeight <= purchaseButtonPosition || scrollPosition >= purchaseButtonPosition + 50) && !stickyPurchase) {
			this.setState({
				stickyPurchase: true,
			});
		}
	}

	allSizesSelected = () => {
		const { selected = {} } = this.state;
		return Object.keys(selected).filter((product) => selected[product] === false).length === 0;
	}

	addToBagHandler = (e) => {
		if (e) e.preventDefault();
		const { setup, checkout, productCache, currentPath } = this.props;
		const { selected, selectedItemSoldOut, variantSelected } = this.state;
		const currentCategory = currentPath.slice(0, currentPath.lastIndexOf('/'));

		// All sizes must be added for purchase to be successful.
		if (this.allSizesSelected() && variantSelected) {
			if (selectedItemSoldOut) {
				Object.keys(selected).forEach((product) => {
					const cachedProduct = productCache[setup.market][setup.pricelist].products[product];
					const size = cachedProduct.sizes.filter((cP) => cP.item === selected[product]).map((cP) => cP.item).join('');
					this.props.productAlert({ selected: size }).then((data) => {
						this.setState({ showNotifyThanks: true });
						window.setTimeout(() => {
							this.clearState();
						}, 3000);
						return data;
					}).catch((err) => {
						console.log('DEBUG: product alert unsuccessfully added to bag');
						Sentry.captureException(err);
					});
				});
				return;
			}

			// If no selection we need to initiate a token.
			// On return from addToBag we can run the other products.
			Object.keys(selected).slice(0, 1).forEach((product) => {
				const cachedProduct = productCache[setup.market][setup.pricelist].products[product];

				this.props.addToBag(selected[product], 1, currentPath, cachedProduct).then((response) => {
					cartAnalytics(cachedProduct, selected[product], setup, {}, currentCategory, response?.selection, checkout.listItemCache);

					if (Object.keys(selected).slice(1).length > 0) {
						Object.keys(selected).slice(1).forEach((extraProduct) => {
							const cachedExtraProduct = productCache[setup.market][setup.pricelist].products[extraProduct];
							this.props.addToBag(selected[extraProduct], 1, currentPath, cachedExtraProduct).then((extraResponse) => {
								cartAnalytics(cachedExtraProduct, selected[extraProduct], setup, {}, currentCategory, extraResponse?.selection, checkout.listItemCache);
								return extraResponse;
							}).catch((err) => {
								console.log('DEBUG: extra product unsuccessfully added to bag', err);
								Sentry.captureException(err);
							});
						});
					}

					return response;
				}).catch((err) => {
					console.log('DEBUG: first product unsuccessfully added to bag', err);
					Sentry.captureException(err);
				});
			});
		} else {
			this.setState({
				formError: true,
			});
			const scrollPosition = window.innerWidth > 600 ? 0 : offset(document.getElementById('purchaseForm')).top - 150;
			scrollTo(400, scrollPosition);
		}
	}

	setSelectedConfiguration = (e) => {
		e.preventDefault();
		const link = e.currentTarget.getAttribute('href');
		this.props.switchVariant(link);
		this.setState({
			variantSelected: true,
		});
	}

	selectSizeHandler = (item, selectedItemSoldOut) => {
		const { relatedProducts = {}, productCache, setup, productId, plp = false, triggerUpdateQuickShopHeight = false } = this.props;
		const [product] = item.split('-');
		if (relatedProducts.bundle) {
			this.setState((prevState) => ({
				formError: false,
				selected: {
					...prevState.selected,
					[product]: item,
				},
				selectedItemSoldOut,
			}));
			return;
		}
		this.setState({
			formError: false,
			selected: {
				[product]: item,
			},
			selectedItemSoldOut,
		});

		if (plp && triggerUpdateQuickShopHeight) {
			setTimeout(() => {
				triggerUpdateQuickShopHeight();
			}, 100);
		}

		if (plp && !selectedItemSoldOut) {
			setTimeout(() => {
				this.props.preSelectSize(item);

				const { products = {} } = productCache?.[setup.market]?.[setup.pricelist];
				const cachedProduct = products[productId];
				if (cachedProduct) this.props.goToPage(cachedProduct.canonicalUri);
			}, 100);
		}
	}

	clearState = () => {
		this.setState({
			formError: false,
			selected: {},
			formErrorHover: false,
			selectedItemSoldOut: false,
			showNotifyThanks: false,
		});
	}

	addToBagMouseEnterHandler = () => {
		if (!this.allSizesSelected()) {
			this.setState({
				formErrorHover: true,
			});
		}
	}

	addToBagMouseLeaveHandler = () => {
		this.setState({
			formErrorHover: false,
		});
	}

	onEmailInputChange = (id, value) => {
		this.props.changeAddressInput('address', 'email', value);
	}

	showSizePopup = (e) => {
		e.preventDefault();
		const id = e.currentTarget.getAttribute('href').slice(1);
		this.setState({
			showPopup: id,
		});
	}

	closeSizePopup = (e) => {
		e.preventDefault();
		this.setState({
			showPopup: false,
		});
	}

	sizeChartClickHandler = (e) => {
		const { productCache, setup, plp = false, triggerUpdateQuickShopHeight = false } = this.props;
		const { products = {} } = productCache[setup.market][setup.pricelist];
		const productId = e.target.closest('div').getAttribute('id').replace('size-chart-product-id-', '');
		const cachedProduct = products[productId];

		const sizeGroups = e.target.closest('tbody').classList.length > 0;
		const size = e.target.closest(sizeGroups ? 'tbody' : 'tr').querySelector('td').textContent;
		const selectItem = cachedProduct.sizes.filter((item) => urlify(item.name.replace(/[-_\s]/g, '')) === urlify(size.replace(/[-_\s]/g, '')))[0];
		const selectedItemSoldOut = selectItem.stock === 0 ? selectItem.item : false;

		if (!selectItem) {
			this.setState({
				showPopup: false,
			});

			return;
		}

		this.setState({
			formError: false,
			showPopup: false,
			selected: {
				[productId]: selectItem.item,
			},
			selectedItemSoldOut,
		});

		if (plp && triggerUpdateQuickShopHeight) {
			setTimeout(() => {
				triggerUpdateQuickShopHeight();
			}, 100);
		}

		if (plp && !selectedItemSoldOut) {
			setTimeout(() => {
				this.addToBagHandler();
			}, 100);
		}
	}

	renderSizes(formattedSizes, cachedProduct, showSizeChart, name, price, notify) {
		const { setup, plp = false } = this.props;
		const { formErrorHover, selected } = this.state;
		const id = cachedProduct.groupId;
		let selectSizeTitle = '';

		// add attribute size here
		if (cachedProduct.features) {
			const hasSelectSizeTitle = Object.keys(cachedProduct.features).filter((f) => cachedProduct.features[f].featureId === 'sizeLabel').map((f) => cachedProduct.features[f].feature);
			if (hasSelectSizeTitle.length > 0) selectSizeTitle = hasSelectSizeTitle.join('');
		}
		if (name) selectSizeTitle += `${name.replace('Marsblade', '')} `;

		return (
			<div key={id} className={[s.sizes, (plp ? s.sizes__plp : null)].join(' ')}>
				{ formattedSizes.length > 1 && showSizeChart && <a href={`#${id}`} className={s.sizes_sizeInfo} onClick={this.showSizePopup}>{`${translate(setup, 'sizeInformation')} ${name.replace('Marsblade', '')}`}</a> }
				{ formattedSizes.length > 1 && (
					<span className={s.sizes_sizeTitle}>{ selectSizeTitle }<Price price={price} /></span>
				) }
				<div className={[s.sizes_choiceContainer, ((formErrorHover) && !selected[id] ? 'u_error' : null)].join(' ')}>
					<div className={[s.sizes_choice, (formattedSizes.length === 1 ? s.sizes_choice__hidden : null)].join(' ')}>
						<Radio plp={plp} id={`${id}_size`} options={formattedSizes} notifyMe={notify} hidden={formattedSizes.length === 1} selected={selected[id]} inputChange={this.selectSizeHandler} variation="size" />
					</div>
				</div>
			</div>
		);
	}

	render() {
		const { setup, checkout, soldOut, productCache, relatedProducts = {}, productId, plp = false, currentVariant, sizeConversions } = this.props;
		const { selected = {}, formError, formErrorHover, selectedItemSoldOut, showPopup, variantSelected, showNotifyThanks, stickyPurchase } = this.state;
		const { filterArrays = {}, products = {} } = productCache[setup.market][setup.pricelist];
		const cachedProduct = products[productId];
		if (!cachedProduct) return null;
		const isAccessory = Object.keys(cachedProduct.categories).filter((cat) => cachedProduct.categories[cat].uri.indexOf('accessories') !== -1).length > 0;
		const itemsLeftLimit = parseInt(setup.statics.itemLeftLimit, 10);
		const hasPreOrder = relatedProducts['pre-order'] || false;
		const linkedPreOrderProduct = hasPreOrder.length > 0 ? products[hasPreOrder[0].product] : null;
		const sizes = cachedProduct.sizes.map((item) => {
			let stock = null;
			if (item.stock < itemsLeftLimit && item.stock > 0 && !isAccessory) stock = translate(setup, 'itemsLeft').replace('%items%', item.stock);
			if (item.stock === 'infinite') stock = translate(setup, 'itemsPreOrder');
			if (item.stock === 0 && !hasPreOrder && cachedProduct?.notify_me !== '1') stock = translate(setup, 'itemsNotifyMe');

			return {
				key: item.item,
				value: item.name,
				stock,
				disabled: item.stock === 0,
			};
		});
		const selectedSize = sizes.filter((size) => Object.values(selected)[0] === size.key);
		let isPreorder = false;

		let buttonText = sizes.length > 1 || sizes[0].value.toLowerCase() !== 'os' ? translate(setup, 'chooseSize') : translate(setup, 'chooseQuantity');
		buttonText = formError || formErrorHover ? buttonText : translate(setup, 'addToBag');
		if (!variantSelected && (formError || formErrorHover)) buttonText = translate(setup, 'chooseConfiguration');
		if (relatedProducts.size && Object.keys(selected).length > 0) {
			const selectedProduct = Object.keys(selected)[0];
			if (selected[selectedProduct] !== false) buttonText = `${buttonText} — ${products[selectedProduct].price.price}`;
		}
		if (selectedSize.length > 0 && selectedSize[0].stock === translate(setup, 'itemsPreOrder')) {
			isPreorder = true;
			buttonText = translate(setup, 'addToBagPreOrderButton');
		}
		if (soldOut || selectedItemSoldOut) buttonText = translate(setup, 'soldOut');
		const selectedItemNotifyMe = selectedItemSoldOut && selectedItemSoldOut.split('-')[0] && products[selectedItemSoldOut.split('-')[0]]?.notify_me !== '1';

		let productsInSimilarSizes = [];
		if (!isAccessory && selectedItemSoldOut) {
			const similarCategory = Object.keys(cachedProduct.categories).filter((cat) => cachedProduct.canonicalUri.indexOf(cachedProduct.categories[cat].uri) !== -1).map((cat) => cachedProduct.categories[cat].uri).join('');
			const [selectedSimilarProduct] = selectedItemSoldOut.split('-');
			const sizeName = products[selectedSimilarProduct].sizes.filter((size) => size.item === selectedItemSoldOut).map((size) => size.name).join('');
			const matchSizesLookup = sizeConversions.reduce((acc, size) => {
				if (!size.matches) acc[size.base] = [size.base];
				else acc[size.base] = [size.base, ...splitNewLine(size.matches)];
				return acc;
			}, {});
			productsInSimilarSizes = (filterArrays?.categories?.[similarCategory] || []).reduce((acc, p) => [...acc, p, ...(products?.[p]?.relatedSizes || [])], []).filter((p) => {
				if (!products[p]) return false;
				if (Object.keys(products[p].categories).filter((cat) => products[p].categories[cat].uri.indexOf('accessories') !== -1).length > 0) return false;
				if (products[p].sizes.filter((size) => matchSizesLookup[sizeName].indexOf(size.name) !== -1 && size.stock > 0).length === 0) return false;
				return true;
			});
		}

		// const features = cachedProduct.features ? Object.keys(cachedProduct.features)
		// 		.filter((f) => {
		//			if (product.features[f].incPricelist && product.features[f].incPricelist.split(/\s*,\s*/).indexOf(setup.pricelist) === -1) return false;
		//			if (product.features[f].incMarket && product.features[f].incMarket.split(/\s*,\s*/).indexOf(setup.market) === -1) return false;
		//			if (product.features[f].incCountry && product.features[f].incCountry.split(/\s*,\s*/).indexOf(setup.country) === -1) return false;
		//			return true;
		//		})
		//		.reduce((acc, f) => {
		// 		if (!acc[cachedProduct.features[f].featureId]) acc[cachedProduct.features[f].featureId] = [];
		// 		acc[cachedProduct.features[f].featureId].push(cachedProduct.features[f]);
		// 		return acc;
		// 	}, {}) : {};

		return (
			<>
				<form id="purchaseForm" className={s.purchase} onSubmit={this.addToBagHandler}>
					{
						/* Not bundle. Main product size guide */
						!relatedProducts.bundle && this.renderSizes(sizes, cachedProduct, (cachedProduct.measurementChartRows && Object.keys(cachedProduct.measurementChartRows).length > 0), (relatedProducts.size ? (cachedProduct.variantName || cachedProduct.name) : ''), cachedProduct.price, cachedProduct?.notify_me !== '1')
					}
					{
						/* Related sizes as in yth/jr */
						relatedProducts.size && (
							relatedProducts.size.map((extraProductId) => {
								const extraItem = products[extraProductId];
								if (!extraItem) return null;
								const extraIsAccessory = Object.keys(extraItem.categories).filter((cat) => extraItem.categories[cat].uri.indexOf('accessories') !== -1).length > 0;
								const extraFormattedSizes = extraItem.sizes.map((item) => {
									let stock = null;
									if (item.stock < itemsLeftLimit && item.stock > 0 && !extraIsAccessory) stock = translate(setup, 'itemsLeft').replace('%items%', item.stock);
									if (item.stock === 'infinite') stock = translate(setup, 'itemsPreOrder');
									if (item.stock === 0 && !hasPreOrder && extraItem?.notify_me !== '1') stock = translate(setup, 'itemsNotifyMe');

									return {
										key: item.item,
										value: item.name,
										stock,
										disabled: item.stock === 0,
									};
								});
								const extraSelectedSize = extraFormattedSizes.filter((size) => Object.values(selected)[0] === size.key);
								if (extraSelectedSize.length > 0 && extraSelectedSize[0].stock === translate(setup, 'itemsPreOrder')) {
									isPreorder = true;
									buttonText = translate(setup, 'addToBagPreOrderButton');
								}

								return this.renderSizes(extraFormattedSizes, extraItem, (extraItem.measurementChartRows && Object.keys(extraItem.measurementChartRows).length > 0), (extraItem.variantName || extraItem.name), extraItem.price, extraItem?.notify_me !== '1');
							})
						)
					}
					{
						/* Is bundle but on related to real product */
						relatedProducts.bundle && relatedProducts.bundle.length < 2 && this.renderSizes(sizes, cachedProduct, (cachedProduct.measurementChartRows && Object.keys(cachedProduct.measurementChartRows).length > 0), '', cachedProduct.price, cachedProduct?.notify_me !== '1')
					}
					{
						/* All other bundle sizes */
						relatedProducts.bundle && (
							relatedProducts.bundle.map((extraProductId) => {
								const extraItem = products[extraProductId];
								if (!extraItem) return null;
								const extraIsAccessory = Object.keys(extraItem.categories).filter((cat) => extraItem.categories[cat].uri.indexOf('accessories') !== -1).length > 0;
								const extraFormattedSizes = extraItem.sizes.map((item) => {
									let stock = null;
									if (item.stock < itemsLeftLimit && item.stock > 0 && !extraIsAccessory) stock = translate(setup, 'itemsLeft').replace('%items%', item.stock);
									if (item.stock === 'infinite') stock = translate(setup, 'itemsPreOrder');
									if (item.stock === 0 && !hasPreOrder && extraItem?.notify_me !== '1') stock = translate(setup, 'itemsNotifyMe');

									return {
										key: item.item,
										value: item.name,
										stock,
										disabled: item.stock === 0,
									};
								});
								const extraSelectedSize = extraFormattedSizes.filter((size) => Object.values(selected)[0] === size.key);
								if (extraSelectedSize.length > 0 && extraSelectedSize[0].stock === translate(setup, 'itemsPreOrder')) {
									isPreorder = true;
									buttonText = translate(setup, 'addToBagPreOrderButton');
								}

								return this.renderSizes(extraFormattedSizes, extraItem, (extraItem.measurementChartRows && Object.keys(extraItem.measurementChartRows).length > 0), extraItem.name, extraItem.price, extraItem?.notify_me !== '1');
							})
						)
					}
					{
						relatedProducts.configurator && (
							<div className={s.options}>
								<span className={s.options_title}>{ translate(setup, 'configuratorTitle') }</span>
								{
									[productId, ...relatedProducts.configurator].sort((a, b) => {
										const cachedItemA = products[a];
										const cachedItemB = products[b];
										const orderA = cachedItemA.features ? Object.keys(cachedItemA.features).filter((f) => cachedItemA.features[f].featureId === 'configurator').map((f) => cachedItemA.features[f].order).join(' ') : '';
										const orderB = cachedItemB.features ? Object.keys(cachedItemB.features).filter((f) => cachedItemB.features[f].featureId === 'configurator').map((f) => cachedItemB.features[f].order).join(' ') : '';
										if (orderA === orderB) return 0;
										return orderB.localeCompare(orderA);
									}).map((p) => {
										const cachedItem = products[p];
										const { feature = '' } = cachedItem.features ? Object.keys(cachedItem.features).filter((f) => cachedItem.features[f].featureId === 'configurator').map((f) => cachedItem.features[f])[0] : {};
										return (
											<div key={feature} className={[s.option, (cachedItem.canonicalUri === currentVariant && variantSelected ? s.option__selected : null)].join(' ')}>
												<Link className={s.option_toggle} href={cachedItem.canonicalUri} onClick={this.setSelectedConfiguration}>{ feature }</Link>
											</div>
										);
									})
								}
							</div>
						)
					}
					{
						(soldOut || selectedItemSoldOut) ? (
							<div className={s.purchase_notification}>
								{
									hasPreOrder > 0 && (
										<>
											{
												linkedPreOrderProduct.uri.indexOf('pre-order') !== -1 && (
													<>
														{ selectedItemSoldOut && <p>{ translate(setup, 'itemSoldOutPreorder') }</p> }
														{ soldOut && <p>{ translate(setup, 'productSoldOutPreorder') }</p> }
													</>
												)
											}
											<Button type="link" clickEventHandler={this.clearState} location={prefix(setup, linkedPreOrderProduct.canonicalUri)} text={linkedPreOrderProduct.uri.indexOf('pre-order') !== -1 ? translate(setup, 'productPreorderLink') : linkedPreOrderProduct.name} />
										</>
									)
								}
								{
									!hasPreOrder && selectedItemNotifyMe && (
										<>
											{ selectedItemSoldOut && <p>{ translate(setup, 'itemSoldOut') }</p> }
											{ soldOut && <p>{ translate(setup, 'productSoldOut') }</p> }
											<div className={s.purchase_notification_inputs}>
												<Input variantClasses={['input__inline']} id="notifyMe" type="email" placeholder={translate(setup, 'emailLabel')} value={checkout.payment.address.email} inputChange={this.onEmailInputChange} />
												<Button variantClasses={['button__inline']} type="submit">{ translate(setup, 'productNotificationSubmit') }</Button>
											</div>
											{ showNotifyThanks && <p className={s.purchase_notification_success}>{ translate(setup, 'productNotificationSuccess') }</p> }
											{ checkout.notification_error && <p className={s.purchase_notification_error}>{ translate(setup, 'productNotificationError') }</p> }
										</>
									)
								}
								{
									!hasPreOrder && !selectedItemNotifyMe && (
										<div id="js-purchaseButtonContainer" className={[s.purchase_submit, s.purchase_submit__disabled].join(' ')}>
											<Button variantClasses={['button__disabled']} disabled>{ buttonText }</Button>
											<p className={s.purchase_submit_soldOut}>{ translate(setup, 'similarErrorSoldOut') }</p>
										</div>
									)
								}
								{
									productsInSimilarSizes.length > 0 && (
										<div className={s.purchase_notification_similar}>
											<h6 className={s.purchase_notification_similar_title}>{ translate(setup, 'similarSizes') }</h6>
											<div className={s.purchase_notification_similar_items}>
												{
													productsInSimilarSizes.map((p) => {
														const similarProduct = products[p];
														let link = similarProduct.canonicalUri;
														if (Object.keys(similarProduct.categories || {}).length === 0 && similarProduct.relatedSizes) {
															// Product is grouped with sizes.
															similarProduct.relatedSizes.forEach((size) => {
																if (Object.keys(products?.[size]?.categories || {}).length > 0) link = products[size].canonicalUri;
															});
														}
														return (
															<Link key={p} className={s.purchase_notification_similar_item} to={prefix(setup, link)}>
																<img className={s.purchase_notification_similar_item_image} {...imageSizeTranslation('3', { url: similarProduct.image[0] }, 'lazy')} alt={`Marsblade - ${similarProduct.name} ${similarProduct.variantName}`} />
																<h6 className={s.purchase_notification_similar_item_title}>{ similarProduct.name }</h6>
																<div className={s.purchase_notification_similar_item_price}>
																	<Price setup={setup} price={similarProduct.price} />
																</div>
															</Link>
														);
													})
												}
											</div>
										</div>
									)
								}
							</div>
						) : (
							<div id="js-purchaseButtonContainer" className={[s.purchase_submit, (plp ? s.purchase_submit__plp : null), (stickyPurchase && setup.breakPoint === 'mobile' ? s.purchase_submit__sticky : null), (formError || formErrorHover ? s.purchase_submit__error : null)].join(' ')}>
								{ isPreorder && <span className={s.purchase_submit_info}>{ translate(setup, 'addToBagPreOrderDescription') }</span> }
								<Button variantClasses={[(isPreorder ? 'button__preorder' : null)]} mouseEnterEventHandler={this.addToBagMouseEnterHandler} mouseLeaveEventHandler={this.addToBagMouseLeaveHandler}>{ buttonText }</Button>
							</div>
						)
					}
					{ checkout.addToBagError && <div className={s.purchase_error}>{ translate(setup, 'addToBagError') }</div> }
				</form>
				<SizeChart plp={plp} setup={setup} product={cachedProduct} sizeChartClickHandler={this.sizeChartClickHandler} isShowing={showPopup === productId} closeOverlayHandler={this.closeSizePopup} />
				{
					relatedProducts.size && relatedProducts.size.map((extraProductId) => {
						const extraItem = products[extraProductId];
						if (!extraItem) return null;
						return <SizeChart key={extraProductId} plp={plp} setup={setup} product={extraItem} sizeChartClickHandler={this.sizeChartClickHandler} isShowing={showPopup === extraProductId} closeOverlayHandler={this.closeSizePopup} />;
					})
				}
			</>
		);
	}
}

const mapState = ({ checkout, productCache, environment }, { setup, currentPath, sizes, productId, currentVariant, switchVariant }) => ({
	checkout,
	productCache,
	sizeConversions: environment.sizeConversions,
	setup,
	currentPath,
	sizes,
	productId,
	currentVariant,
	switchVariant,
});

export default hot(module)(connect(mapState, { addToBag, productAlert, changeAddressInput, preloadPage, preSelectSize, goToPage })(Purchase));
